import { createGameChannel, supabase } from './supabase';
import { useGameStore } from '../store/gameStore';
import { RealtimeChannel } from '@supabase/supabase-js';
import { ItemData, ItemType } from '../config/items';

export interface ItemCollectPayload {
  collector_id: string;
  collected_ids: string[];
  effect: ItemType | null;
  bomb: boolean;
}

export interface BombFirePayload {
  owner_id: string;
  x: number;
  y: number;
  color: number;
}

export interface ItemHandlers {
  onSpawn: (items: ItemData[]) => void;
  onDespawn: (ids: string[]) => void;
  onSnapshot: (items: ItemData[]) => void;
  onCollect: (payload: ItemCollectPayload) => void;
  onBombFire: (payload: BombFirePayload) => void;
}

// 활성 효과의 원격 동기화 표현 (move/bot:move payload에 동봉)
export interface RemoteEffect {
  type: ItemType;
  remainingMs: number;
}

interface PlayerMovePayload {
  user_id: string;
  session_id?: string;
  x: number;
  y: number;
  direction?: number;
  // trail은 대역폭 절약을 위해 일부 패킷에만 포함된다 (없으면 수신측이 기존 trail 유지)
  trail?: { x: number; y: number }[];
  // 'world' = 부드러운 월드 좌표 (신규), 없으면 'grid' (구버전 호환 - 타일 좌표)
  trail_space?: 'grid' | 'world';
  nickname?: string;
  color?: string;
  score?: number;
  territory_percent?: number;
  // 활성 아이템 효과 (null = 효과 없음)
  effect?: RemoteEffect | null;
  seq?: number;
  sent_at?: number;
}

interface BotMovePayload {
  bot_id: string;
  // 이 봇을 시뮬레이션 중인 호스트 클라이언트의 user_id (호스트 중복 시 작은 ID가 이긴다)
  host_id?: string;
  x: number;
  y: number;
  direction: number;
  // trail은 대역폭 절약을 위해 일부 패킷에만 포함된다 (없으면 수신측이 기존 trail 유지)
  trail?: { x: number; y: number }[];
  effect?: RemoteEffect | null;
  nickname: string;
  color: number;
}

interface TerritoryUpdatePayload {
  user_id: string;
  trail_points: { x: number; y: number }[];
  color: number;
}

interface TerritorySnapshotPayload {
  // 영역 소유자 (플레이어 또는 bot_N)
  user_id: string;
  color: number;
  // polygon-clipping MultiPolygon 형식: [폴리곤][링][좌표][x,y]
  polygon: [number, number][][][];
}

interface MapUpdatePayload {
  tiles: {
    x: number;
    y: number;
    layer: number;
    owner_id: string | null;
    color?: number;
  }[];
}

export class SocketService {
  private channel: RealtimeChannel;
  private roomId: string;
  private userId: string;
  private sessionId: string;
  private nickname: string;
  private color: string;
  private onMapUpdate: (payload: MapUpdatePayload) => void;
  private onBotMove?: (payload: BotMovePayload) => void;
  private onTerritoryUpdate?: (payload: TerritoryUpdatePayload) => void;
  private onTerritorySnapshot?: (payload: TerritorySnapshotPayload) => void;
  private onPresenceSync?: (players: Map<string, any>) => void;
  // 다른 플레이어가 내 꼬리를 끊어 나를 죽였을 때 호출
  private onLocalPlayerKilled?: (killerId?: string) => void;
  // 비호스트가 봇 꼬리를 밟아 봇 사망을 요청했을 때 호출 (호스트가 처리)
  private onBotKillRequest?: (payload: { bot_id: string; killer_id: string }) => void;
  // 호스트가 봇 구성 축소로 봇을 제거했을 때 호출 (비호스트가 처리)
  private onBotRemove?: (payload: { bot_id: string }) => void;
  // 아이템 이벤트 핸들러 (생성자 비대화를 막기 위해 setter로 주입)
  private itemHandlers?: ItemHandlers;
  private isConnected: boolean = false;
  private disconnectRequested: boolean = false;
  private reconnecting: boolean = false;
  private reconnectTimer?: number;
  private moveSequence: number = 0;
  private removedPlayers: Map<string, { sessionId?: string; expiresAt: number }> = new Map();
  private readonly removedPlayerTtlMs: number = 15000;

  constructor(
    roomId: string,
    userId: string,
    sessionId: string,
    nickname: string,
    color: string,
    onMapUpdate: (payload: MapUpdatePayload) => void,
    onBotMove?: (payload: BotMovePayload) => void,
    onTerritoryUpdate?: (payload: TerritoryUpdatePayload) => void,
    onPresenceSync?: (players: Map<string, any>) => void,
    onLocalPlayerKilled?: (killerId?: string) => void,
    onTerritorySnapshot?: (payload: TerritorySnapshotPayload) => void,
    onBotKillRequest?: (payload: { bot_id: string; killer_id: string }) => void,
    onBotRemove?: (payload: { bot_id: string }) => void
  ) {
    this.roomId = roomId;
    this.userId = userId;
    this.sessionId = sessionId;
    this.nickname = nickname;
    this.color = color;
    this.onMapUpdate = onMapUpdate;
    this.onBotMove = onBotMove;
    this.onTerritoryUpdate = onTerritoryUpdate;
    this.onTerritorySnapshot = onTerritorySnapshot;
    this.onPresenceSync = onPresenceSync;
    this.onLocalPlayerKilled = onLocalPlayerKilled;
    this.onBotKillRequest = onBotKillRequest;
    this.onBotRemove = onBotRemove;
    this.channel = createGameChannel(roomId, userId);
  }

  /** 아이템 이벤트 핸들러 주입 (connect 전에 호출) */
  public setItemHandlers(handlers: ItemHandlers): void {
    this.itemHandlers = handlers;
  }

  public connect() {
    this.channel
      .on('broadcast', { event: 'player:move' }, ({ payload }) => {
        this.handlePlayerMove(payload as PlayerMovePayload);
      })
      .on('broadcast', { event: 'player:join' }, ({ payload }) => {
        this.handlePlayerJoin(payload);
      })
      .on('broadcast', { event: 'player:leave' }, ({ payload }) => {
        this.handlePlayerLeave(payload);
      })
      .on('broadcast', { event: 'map:update' }, ({ payload }) => {
        this.onMapUpdate(payload as MapUpdatePayload);
      })
      .on('broadcast', { event: 'player:death' }, ({ payload }) => {
        this.handlePlayerDeath(payload);
      })
      .on('broadcast', { event: 'player:trail-clear' }, ({ payload }) => {
        this.handlePlayerTrailClear(payload);
      })
      .on('broadcast', { event: 'bot:move' }, ({ payload }) => {
        if (this.onBotMove) {
          this.onBotMove(payload as BotMovePayload);
        }
      })
      .on('broadcast', { event: 'territory:update' }, ({ payload }) => {
        if (this.onTerritoryUpdate && payload.user_id !== this.userId) {
          this.onTerritoryUpdate(payload as TerritoryUpdatePayload);
        }
      })
      .on('broadcast', { event: 'territory:snapshot' }, ({ payload }) => {
        if (this.onTerritorySnapshot && payload.user_id !== this.userId) {
          this.onTerritorySnapshot(payload as TerritorySnapshotPayload);
        }
      })
      .on('broadcast', { event: 'bot:death' }, ({ payload }) => {
        if (this.onBotKillRequest && payload.killer_id !== this.userId) {
          this.onBotKillRequest(payload as { bot_id: string; killer_id: string });
        }
      })
      .on('broadcast', { event: 'bot:remove' }, ({ payload }) => {
        if (this.onBotRemove && payload.host_id !== this.userId) {
          this.onBotRemove(payload as { bot_id: string });
        }
      })
      .on('broadcast', { event: 'item:spawn' }, ({ payload }) => {
        if (payload.host_id !== this.userId) {
          this.itemHandlers?.onSpawn(payload.items as ItemData[]);
        }
      })
      .on('broadcast', { event: 'item:despawn' }, ({ payload }) => {
        if (payload.host_id !== this.userId) {
          this.itemHandlers?.onDespawn(payload.ids as string[]);
        }
      })
      .on('broadcast', { event: 'item:snapshot' }, ({ payload }) => {
        if (payload.host_id !== this.userId) {
          this.itemHandlers?.onSnapshot(payload.items as ItemData[]);
        }
      })
      .on('broadcast', { event: 'item:collect' }, ({ payload }) => {
        // 수집자 본인도 자기 collect를 다시 받지만, 이미 로컬 처리했으므로 무시
        if (payload.collector_id !== this.userId) {
          this.itemHandlers?.onCollect(payload as ItemCollectPayload);
        }
      })
      .on('broadcast', { event: 'bomb:fire' }, ({ payload }) => {
        // 발사자 본인은 이미 로컬에서 미사일을 생성했으므로 무시
        if (payload.owner_id !== this.userId) {
          this.itemHandlers?.onBombFire(payload as BombFirePayload);
        }
      })
      // Presence 이벤트: 플레이어 동기화를 위한 신뢰할 수 있는 상태 관리
      .on('presence', { event: 'sync' }, () => {
        const state = this.channel.presenceState();
        console.log('Presence sync:', state);
        this.handlePresenceSync(state);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('Presence join:', key, newPresences);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('Presence leave:', key, leftPresences);
        // 떠난 플레이어 제거
        leftPresences.forEach((presence: any) => {
          if (presence.user_id && presence.user_id !== this.userId) {
            this.markPlayerRemoved(presence.user_id, presence.session_id);
          }
        });
      })
      .subscribe(async (status, err) => {
        if (status === 'SUBSCRIBED') {
          this.isConnected = true;
          this.disconnectRequested = false;
          this.reconnecting = false;
          console.log('Connected to game room:', this.roomId);

          // Presence에 자신의 상태 등록
          await this.channel.track({
            user_id: this.userId,
            session_id: this.sessionId,
            nickname: this.nickname,
            color: this.color,
            online_at: new Date().toISOString(),
          });

          // 입장 알림 (broadcast도 함께 전송)
          await this.channel.send({
            type: 'broadcast',
            event: 'player:join',
            payload: {
              user_id: this.userId,
              session_id: this.sessionId,
              nickname: this.nickname,
              color: this.color
            }
          });
        } else if (status === 'CLOSED') {
          this.isConnected = false;
          if (!this.disconnectRequested) {
            console.warn('WebSocket connection closed, attempting to reconnect...');
            this.scheduleReconnect(1000);
          }
        } else if (status === 'CHANNEL_ERROR') {
          this.isConnected = false;
          console.error('WebSocket channel error:', err);
          if (!this.disconnectRequested) {
            this.scheduleReconnect(2000);
          }
        }
      });
  }

  private scheduleReconnect(delayMs: number) {
    if (this.reconnectTimer !== undefined) {
      window.clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      if (!this.disconnectRequested && !this.reconnecting) {
        this.reconnecting = true;
        this.channel.subscribe();
      }
    }, delayMs);
  }

  private markPlayerRemoved(userId: string, sessionId?: string) {
    if (!userId || userId === this.userId) return;

    this.removedPlayers.set(userId, {
      sessionId,
      expiresAt: Date.now() + this.removedPlayerTtlMs,
    });
    useGameStore.getState().removePlayer(userId);
  }

  private isPlayerRemoved(userId: string, sessionId?: string): boolean {
    const removed = this.removedPlayers.get(userId);
    if (!removed) return false;

    if (removed.expiresAt <= Date.now()) {
      this.removedPlayers.delete(userId);
      return false;
    }

    return !sessionId || !removed.sessionId || removed.sessionId === sessionId;
  }

  /**
   * 죽거나 떠난 플레이어인지 확인 (tombstone 조회).
   * 사망 직전에 발사된 영역 스냅샷/확장 패킷이 늦게 도착해
   * 죽은 플레이어의 영역을 부활시키는 것을 막는 데 사용한다.
   */
  public isPlayerTombstoned(userId: string): boolean {
    return this.isPlayerRemoved(userId);
  }

  private clearRemovedPlayer(userId: string, sessionId?: string) {
    const removed = this.removedPlayers.get(userId);
    if (!removed) return;

    if (sessionId && removed.sessionId && removed.sessionId !== sessionId) {
      this.removedPlayers.delete(userId);
    }
  }

  private handlePresenceSync(state: Record<string, any[]>) {
    const players = new Map<string, any>();

    // Presence 상태에서 모든 플레이어 정보 추출
    Object.values(state).forEach((presences) => {
      presences.forEach((presence) => {
        if (presence.user_id && presence.user_id !== this.userId) {
          if (this.isPlayerRemoved(presence.user_id, presence.session_id)) {
            return;
          }

          players.set(presence.user_id, {
            id: presence.user_id,
            sessionId: presence.session_id,
            nickname: presence.nickname || 'Unknown',
            color: presence.color || '#4D96FF',
          });
        }
      });
    });

    console.log('Players from presence:', players.size);

    // 게임 스토어에 플레이어 추가
    const store = useGameStore.getState();
    players.forEach((playerData, playerId) => {
      if (!store.players.has(playerId)) {
        store.addPlayer({
          id: playerId,
          sessionId: playerData.sessionId,
          nickname: playerData.nickname,
          color: playerData.color,
          x: 0,
          y: 0,
          score: 0,
          isAlive: true,
          trail: [],
          hasPosition: false
        });
      } else {
        store.updatePlayer(playerId, {
          nickname: playerData.nickname,
          sessionId: playerData.sessionId,
          color: playerData.color,
          isAlive: true
        });
      }
    });

    store.players.forEach((_player, playerId) => {
      if (playerId !== this.userId && !players.has(playerId)) {
        store.removePlayer(playerId);
      }
    });

    // 콜백 호출
    if (this.onPresenceSync) {
      this.onPresenceSync(players);
    }
  }

  public async disconnect() {
    this.disconnectRequested = true;
    this.reconnecting = false;
    if (this.reconnectTimer !== undefined) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    if (this.isConnected) {
      // 다른 플레이어에게 퇴장 알림을 먼저 전송한 뒤 Presence에서 제거한다.
      await this.channel.send({
        type: 'broadcast',
        event: 'player:leave',
        payload: {
          user_id: this.userId,
          session_id: this.sessionId,
        }
      });

      await this.channel.untrack();
    }

    this.isConnected = false;
    await supabase.removeChannel(this.channel);
    console.log('Disconnected from game room:', this.roomId);
  }

  public sendMove(
    x: number,
    y: number,
    direction: number,
    trail: { x: number; y: number }[] | undefined,
    nickname: string,
    color: string,
    score: number = 0,
    territoryPercent: number = 0,
    effect: RemoteEffect | null = null
  ) {
    // 연결되지 않으면 전송하지 않음 (REST 폴백 방지)
    if (!this.isConnected) return;
    this.moveSequence += 1;

    const payload: PlayerMovePayload = {
      user_id: this.userId,
      session_id: this.sessionId,
      x: Math.round(x),
      y: Math.round(y),
      direction: Math.round(direction * 1000) / 1000,
      nickname,
      color,
      score,
      territory_percent: territoryPercent,
      effect,
      seq: this.moveSequence,
      sent_at: Date.now()
    };

    if (trail) {
      // 부드러운 월드 좌표 trail (그리드 좌표는 수신측에서 계단/지그재그로 보였음)
      payload.trail = trail.map((point) => ({
        x: Math.round(point.x),
        y: Math.round(point.y),
      }));
      payload.trail_space = 'world';
    }

    this.channel.send({
      type: 'broadcast',
      event: 'player:move',
      payload
    });
  }

  public sendTrailClear() {
    if (!this.isConnected) return;

    return this.channel.send({
      type: 'broadcast',
      event: 'player:trail-clear',
      payload: {
        user_id: this.userId,
        session_id: this.sessionId,
        sent_at: Date.now()
      }
    });
  }

  public sendPlayerDeath(
    victimId: string = this.userId,
    killerId?: string,
    type?: string,
    victimSessionId?: string
  ) {
    if (!this.isConnected) return Promise.resolve();

    return this.channel.send({
      type: 'broadcast',
      event: 'player:death',
      payload: {
        victim_id: victimId,
        // 피해자 세션을 함께 보내야 같은 user_id의 "다음 세션"이 tombstone에 막히지 않는다
        session_id: victimId === this.userId ? this.sessionId : victimSessionId,
        killer_id: killerId,
        collision_type: type,
        sent_at: Date.now()
      }
    });
  }

  public sendBotMove(
    botId: string,
    x: number,
    y: number,
    direction: number,
    trail: { x: number; y: number }[] | undefined,
    nickname: string,
    color: number,
    effect: RemoteEffect | null = null
  ) {
    // 연결되지 않으면 전송하지 않음 (REST 폴백 방지)
    if (!this.isConnected) return;

    const payload: BotMovePayload = {
      bot_id: botId,
      host_id: this.userId,
      // 풀 정밀도 float 좌표는 JSON 페이로드를 수 배로 키우므로 반올림해서 전송
      x: Math.round(x),
      y: Math.round(y),
      direction: Math.round(direction * 1000) / 1000,
      effect,
      nickname,
      color
    };

    if (trail) {
      payload.trail = trail.map((point) => ({
        x: Math.round(point.x),
        y: Math.round(point.y),
      }));
    }

    this.channel.send({
      type: 'broadcast',
      event: 'bot:move',
      payload
    });
  }

  public sendTerritoryUpdate(
    trailPoints: { x: number; y: number }[],
    color: number,
    ownerId: string = this.userId
  ) {
    // 연결되지 않으면 전송하지 않음 (REST 폴백 방지)
    if (!this.isConnected) return;

    this.channel.send({
      type: 'broadcast',
      event: 'territory:update',
      payload: {
        // ownerId로 봇 영역도 동기화할 수 있다 (예: bot_0)
        user_id: ownerId,
        trail_points: trailPoints.map((point) => ({
          x: Math.round(point.x),
          y: Math.round(point.y),
        })),
        color
      }
    });
  }

  /**
   * 영역 전체 스냅샷 전송 (늦게 입장한 클라이언트가 기존 영역을 볼 수 있게)
   * ownerId로 봇 영역(bot_N)도 보낼 수 있다 - 호스트가 담당.
   */
  public sendTerritorySnapshot(
    ownerId: string,
    color: number,
    polygon: [number, number][][][]
  ) {
    if (!this.isConnected) return;
    if (!polygon || polygon.length === 0) return;

    this.channel.send({
      type: 'broadcast',
      event: 'territory:snapshot',
      payload: {
        user_id: ownerId,
        color,
        polygon
      }
    });
  }

  private handlePlayerMove(payload: PlayerMovePayload) {
    if (payload.user_id === this.userId) return; // 자신의 이동은 무시
    if (this.isPlayerRemoved(payload.user_id, payload.session_id)) return;
    this.clearRemovedPlayer(payload.user_id, payload.session_id);

    const store = useGameStore.getState();
    const existingPlayer = store.players.get(payload.user_id);

    if (existingPlayer) {
      // seq는 게임 세션마다 1부터 다시 시작하므로, 같은 세션의 패킷끼리만 비교해야 한다.
      // 세션 구분 없이 비교하면 재입장한 플레이어의 새 패킷이 전부 버려져 영구 멈춤이 된다.
      const sameSession =
        !payload.session_id ||
        !existingPlayer.sessionId ||
        payload.session_id === existingPlayer.sessionId;

      if (
        sameSession &&
        payload.seq !== undefined &&
        existingPlayer.lastSeq !== undefined &&
        payload.seq <= existingPlayer.lastSeq
      ) {
        return;
      }

      store.updatePlayer(payload.user_id, {
        sessionId: payload.session_id ?? existingPlayer.sessionId,
        x: payload.x,
        y: payload.y,
        direction: payload.direction,
        score: payload.score ?? existingPlayer.score,
        territoryPercent: payload.territory_percent ?? existingPlayer.territoryPercent ?? 0,
        // trail 미포함 패킷이면 기존 trail 유지 ([]는 명시적 제거이므로 그대로 반영)
        trail: payload.trail ?? existingPlayer.trail,
        trailSpace: payload.trail ? (payload.trail_space ?? 'grid') : existingPlayer.trailSpace,
        effect: payload.effect ?? null,
        hasPosition: true,
        isAlive: true,
        lastSeenAt: Date.now(),
        lastSeq: payload.seq
      });
    } else {
      // 새로운 플레이어 추가
      store.addPlayer({
        id: payload.user_id,
        sessionId: payload.session_id,
        nickname: payload.nickname || 'Unknown',
        color: payload.color || '#4D96FF',
        x: payload.x,
        y: payload.y,
        direction: payload.direction || 0,
        score: payload.score ?? 0,
        territoryPercent: payload.territory_percent ?? 0,
        isAlive: true,
        trail: payload.trail || [],
        trailSpace: payload.trail_space ?? 'grid',
        effect: payload.effect ?? null,
        hasPosition: true,
        lastSeenAt: Date.now(),
        lastSeq: payload.seq
      });
    }
  }

  private handlePlayerJoin(payload: any) {
    console.log('Player joined:', payload.user_id, payload.nickname, payload.color);
    if (payload.user_id === this.userId) return;
    if (this.isPlayerRemoved(payload.user_id, payload.session_id)) return;
    this.clearRemovedPlayer(payload.user_id, payload.session_id);

    const store = useGameStore.getState();
    if (!store.players.has(payload.user_id)) {
      store.addPlayer({
        id: payload.user_id,
        sessionId: payload.session_id,
        nickname: payload.nickname || 'Unknown',
        color: payload.color || '#4D96FF',
        x: 0,
        y: 0,
        score: 0,
        isAlive: true,
        trail: [],
        hasPosition: false
      });
    }
  }

  private handlePlayerLeave(payload: any) {
    console.log('Player left:', payload.user_id);
    if (payload.user_id === this.userId) return;
    this.markPlayerRemoved(payload.user_id, payload.session_id);
  }

  private handlePlayerDeath(payload: any) {
    console.log('Player died:', payload.victim_id, 'killed by:', payload.killer_id);

    if (payload.victim_id !== this.userId) {
      this.markPlayerRemoved(payload.victim_id, payload.session_id);
    } else if (payload.killer_id && payload.killer_id !== this.userId) {
      // 다른 플레이어가 내 꼬리를 끊어 나를 죽인 경우 → 내 클라이언트도 게임오버 처리
      // (이전에는 이 경로가 없어서 원격 킬이 피해자에게 적용되지 않았다)
      this.onLocalPlayerKilled?.(payload.killer_id);
    }
  }

  /**
   * 원격 플레이어 처치: 사망 브로드캐스트를 보내고 로컬에서도 즉시 제거한다.
   * 피해자 클라이언트는 player:death(victim=자신)를 받아 스스로 게임오버 처리한다.
   * killerId 기본값은 자신이며, 호스트가 봇을 대신해 판정할 때는 봇 ID를 넘긴다.
   */
  public killRemotePlayer(victimId: string, killerId: string = this.userId): Promise<unknown> {
    const store = useGameStore.getState();
    const victim = store.players.get(victimId);
    const sendResult = this.sendPlayerDeath(victimId, killerId, 'TRAIL_CUT', victim?.sessionId);

    // tombstone 등록 + 스토어 제거 (피해자의 in-flight 이동 패킷으로 되살아나지 않게)
    this.markPlayerRemoved(victimId, victim?.sessionId);

    return Promise.resolve(sendResult);
  }

  /**
   * 봇 제거 알림 (호스트 → 전체). 접속자 수에 따른 봇 구성 축소 시 사용.
   */
  public sendBotRemove(botId: string) {
    if (!this.isConnected) return;

    this.channel.send({
      type: 'broadcast',
      event: 'bot:remove',
      payload: {
        bot_id: botId,
        host_id: this.userId
      }
    });
  }

  // === 아이템 송신 (호스트 권위: spawn/despawn/snapshot, 누구나: collect) ===

  public sendItemSpawn(items: ItemData[]) {
    if (!this.isConnected) return;
    this.channel.send({
      type: 'broadcast',
      event: 'item:spawn',
      payload: { host_id: this.userId, items },
    });
  }

  public sendItemDespawn(ids: string[]) {
    if (!this.isConnected) return;
    this.channel.send({
      type: 'broadcast',
      event: 'item:despawn',
      payload: { host_id: this.userId, ids },
    });
  }

  public sendItemSnapshot(items: ItemData[]) {
    if (!this.isConnected) return;
    this.channel.send({
      type: 'broadcast',
      event: 'item:snapshot',
      payload: { host_id: this.userId, items },
    });
  }

  public sendItemCollect(payload: ItemCollectPayload) {
    if (!this.isConnected) return;
    this.channel.send({
      type: 'broadcast',
      event: 'item:collect',
      payload,
    });
  }

  public sendBombFire(payload: BombFirePayload) {
    if (!this.isConnected) return;
    this.channel.send({
      type: 'broadcast',
      event: 'bomb:fire',
      payload,
    });
  }

  /**
   * 봇 사망 요청 (비호스트 → 호스트). 봇 시뮬레이션 권위는 호스트에 있으므로
   * 비호스트는 판정만 하고 실제 사망/리스폰 처리는 호스트에게 맡긴다.
   */
  public sendBotDeath(botId: string) {
    if (!this.isConnected) return;

    this.channel.send({
      type: 'broadcast',
      event: 'bot:death',
      payload: {
        bot_id: botId,
        killer_id: this.userId
      }
    });
  }

  private handlePlayerTrailClear(payload: any) {
    if (payload.user_id === this.userId) return;

    const store = useGameStore.getState();
    if (!this.isPlayerRemoved(payload.user_id, payload.session_id) && store.players.has(payload.user_id)) {
      store.updatePlayer(payload.user_id, {
        trail: [],
        lastSeenAt: Date.now()
      });
    }
  }
}
